/**
 * Global axios response interceptor — auto-handles JWT expiry.
 *
 * Problem we're solving: when a user's JWT expires the backend returns
 * `401 { detail: "Token has expired" }` on every API call. Without this
 * interceptor every page bound to the user sees its own "Token has
 * expired" toast and the app appears broken — the only fix is to log
 * out manually. (Reported by a real Allure user on Android, Feb 2026.)
 *
 * What this interceptor does:
 *  - Catches every 401 globally.
 *  - If the response detail is "Token has expired" (or token absent),
 *    it clears the stale token + user from localStorage, fires a
 *    friendly toast ONCE, then hard-redirects to /login. The hard
 *    redirect (vs `navigate(-1)` or react-router) guarantees we shed
 *    every stale piece of in-memory app state including Socket.IO
 *    connections, Airbridge identity, and cached React Query data.
 *  - Auth endpoints themselves (POST /api/auth/login, register,
 *    forgot-password, etc.) are exempt — a 401 there means "bad
 *    credentials", not "session expired", and the page already
 *    surfaces a contextual error.
 *
 * Side-effects are intentionally idempotent: re-installing the
 * interceptor (e.g. by HMR) eject()s the previous one first.
 */
import axios from 'axios';
import { toast } from 'sonner';
import { clearAuthSession } from './authStorage';

let installedInterceptorId = null;

// Endpoints where a 401 is "bad credentials", not "session expired".
// We let the page's own error handling deal with the message.
const AUTH_PUBLIC_PATHS = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
  '/api/auth/verify-email',
  '/api/auth/resend-verification',
  '/api/auth/2fa/verify',
  '/api/auth/google/session',
  '/api/auth/google/complete',
  '/api/auth/google/oauth-token',
];

const isAuthEndpoint = (url) => {
  if (!url) return false;
  return AUTH_PUBLIC_PATHS.some((path) => url.includes(path));
};

// Guard against firing the toast + redirect multiple times when several
// in-flight requests fail with 401 simultaneously.
let handlingExpiry = false;

const handleExpiredToken = () => {
  if (handlingExpiry) return;
  handlingExpiry = true;
  // Wipe both localStorage AND the native Preferences mirror so the next
  // cold launch doesn't try to hydrate a token we already know is dead.
  clearAuthSession();
  try {
    toast.error('Your session ended. Please sign in again.', {
      id: 'allure-session-expired',
      duration: 5000,
    });
  } catch {
    /* toast might not be mounted yet */
  }
  // Hard redirect to /login (or /unlock if a passkey is registered on
  // this device) so every in-memory cache is wiped. setTimeout gives the
  // toast 50ms to render before the navigation. The app's own
  // `lastActiveRole` memory already brings the user back to the right
  // shell on re-login, so we don't need to attach `?next=`.
  setTimeout(() => {
    if (typeof window !== 'undefined') {
      let target = '/login';
      try {
        if (window.localStorage.getItem('allure_has_passkey') === 'true') {
          target = '/unlock';
        }
      } catch {
        /* localStorage unavailable — fall back to /login */
      }
      window.location.replace(target);
    }
    handlingExpiry = false;
  }, 50);
};

export function installAxiosAuthInterceptor() {
  if (installedInterceptorId !== null) {
    axios.interceptors.response.eject(installedInterceptorId);
    installedInterceptorId = null;
  }
  installedInterceptorId = axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || '';
      const url = error?.config?.url || '';
      if (status === 401 && !isAuthEndpoint(url)) {
        // Recognise every FastAPI signal that the user's session is gone:
        //   - "Token has expired" — the JWT timed out
        //   - "Invalid token" — JWT secret rotated by a redeploy, or
        //     the stored value got corrupted
        //   - "Missing or invalid authorization header" — token cleared mid-flight
        //   - "Could not validate credentials" — generic decode failure
        // Anything else (e.g. permission denied on a specific endpoint
        // for a still-valid session) is left alone.
        const looksLikeExpiredSession = (
          /token has expired/i.test(detail)
          || /invalid token/i.test(detail)
          || /missing or invalid authorization/i.test(detail)
          || /could not validate/i.test(detail)
        );
        if (looksLikeExpiredSession) {
          handleExpiredToken();
        }
      }
      return Promise.reject(error);
    },
  );
  return installedInterceptorId;
}
